export const NULLISH_VALS = [
  undefined,
  null,
];
export const FALSY_VALS = [
  ...NULLISH_VALS,
  false,
  0,
  -0,
  ``,
  NaN,
];

/**
 * Returns true if `thing` is undefined or null, else `false`.
 *
 * @param { unknown } thing
 *
 * @returns { boolean }
 */
export const nullish = thing => NULLISH_VALS.includes(thing);

/**
 * Returns `true` if `thing` is [falsy](https://developer.mozilla.org/en-US/docs/Glossary/Falsy), else `false`.
 *
 * @param  { unknown } thing
 *
 * @returns { boolean }
 */
export const falsy = thing => FALSY_VALS.includes(thing);

/**
 * Returns `true` if `thing` is [truthy](https://developer.mozilla.org/en-US/docs/Glossary/Truthy), else `false`.
 *
 * @param { unknown } thing
 *
 * @returns { boolean }
 */
export const truthy = thing => !FALSY_VALS.includes(thing);

/**
 * Returns the first parameter supplied where `nullish(param)` is `false`.
 *
 * @param { ...unknown } things
 */
export const fallback = (...things) => [
  ...things,
].find(thing => !nullish(thing));

/**
 * Returns the first parameter supplied where `falsy(param)` is `false`.
 *
 * @param  { ...unknown } things
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const fallbackFalsy = (...things) => [
  ...things,
].find(thing => !falsy(thing));

/**
 * Workaround for contexts where [optional chaining](https://babeljs.io/docs/en/babel-plugin-proposal-optional-chaining)
 * is not accessible (such as Vue's rendering context).
 *
 * Takes a string representation of an object property access,
 * and returns that value if it and all parents en-route are not nullish,
 * else returns `undefined`.
 * i.e:
 * ```
 * const o = { a: { b: 10, c: null }};
 * nullishChain(o, `a.b`);    // returns '10'
 * nullishChain(o, `a.c`);    // returns `null`
 * nullishChain(o, `a.c.d`);  // returns `undefined`
 * ```
 *
 * @param { Object.<string, unknown> } obj
 * @param { string } key_str
 *
 * @returns { (unknown | undefined) }
 */
// eslint-disable-next-line snakecasejs/snakecasejs
export const nullishChain = (obj, key_str) => {
  if (nullish(obj)) {
    return undefined;
  }
  const _n_chain = (obj, key_arr) => {
    if (typeof obj !== `object`
		|| !nullish(obj) && key_arr.length === 0) {
      return obj;
    }
    if (nullish(obj[key_arr[0]])) {
      return obj[key_arr[0]];
    }
    return _n_chain(obj[key_arr[0]], key_arr.slice(1));
  };
  return _n_chain(obj[key_str.split(`.`)[0]], key_str.split(`.`).slice(1));
};

/**
 * Makes an 'enum' (just a frozen object), with the same behavior as a Typescript enum.
 *
 * Accepts an array containing keys and/or key-value pairs, where values are `string`s or `number`s:
 *
 * ```js
 * const MY_ENUM = makeEnum(['A', 'Be Cool!', ['Cee', 10], ['D', 'ok']]);
 *
 * MY_ENUM.A; // Symbol(A)
 * const a = MY_ENUM.A; // Symbol(A)
 * MY_ENUM[a]; // 'A'
 *
 * // this doesn't work as symbols are unique:
 * MY_ENUM[Symbol('A')];
 * ```
 *
 * The enum is just an object:
 *
 * ```js
 * {
 *   A: Symbol(A),
 *   BE_COOL: Symbol(Be Cool!),
 *   CEE: Symbol(10),
 *   D: Symbol(ok),
 *   [Symbol(A)]: 'A',
 *   [Symbol(Be Cool!)]: 'BE_COOL',
 *   [Symbol(10)]: 'CEE',
 *   [Symbol(ok)]: 'D'
 * }
 * ```
 *
 * Whitespace is replaces with underscores, and non-alphanumeric characters are removed.
 *
 * @param { ([string, string|number|undefined] | string)[] } entries
 *
 * @returns { Readonly<{[key: string|Symbol]: string|Symbol }> }
 */
export const makeEnum = (entries) => {
  // guarantee array of [key, val] pairs, keys formatted, vals as Symbols
  const parsed = entries.map((entry_or_key) => {
    const key_only = typeof entry_or_key === 'string';

    const key = (key_only ? entry_or_key : entry_or_key[0]);
    const val = key_only ? Symbol(key) : Symbol(entry_or_key[1]);

    return [
      key.replace(/\s/g, '_').replace(/\W/g, '').toUpperCase(),
      val,
    ];
  });

  const out_enum = parsed.reduce((out_enum, [key, val]) => {
    out_enum = {
      ...out_enum,
      [key]: val,
      [val]: key,
    };

    return out_enum;
  }, {});

  return Object.freeze(out_enum);
};
